"use client"

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react"
import { getToken, removeToken, getStoredUser, setStoredUser, type StoredUser } from "@/lib/api"
import { getUserInfo, type UserInfoResponse } from "@/lib/api/auth"
import { generateAvatarDataUri, getDisplayName } from "@/lib/avatar"
import { analytics } from "@/src/utils/analytics"
import { useChatStore } from "@/src/workplace/chat/store/chat-store"
import { clearCommunitySelfCache } from "@/lib/community/community-api"
import { useUserStore } from "@/lib/community/store/user-store"
import { useFeedStore } from "@/lib/community/store/feed-store"
import { usePostStore } from "@/lib/community/store/post-store"
import { invalidateCache } from "@/lib/community/service/request-dedup"
import { loadAndCachePermissions, clearPermissionsCache } from "@/lib/api/permissions"

/* ── Context shape ── */
interface AuthContextValue {
  isLoggedIn: boolean
  currentUser: StoredUser | null
  avatarUrl: string | null
  showLoginModal: boolean
  openLogin: () => void
  closeLogin: () => void
  onLoginSuccess: () => void
  logout: () => void
  refreshAvatar: () => void
  /** 用户菜单/按钮权限是否已拉取完成（用于按钮可见性判断） */
  permissionsReady: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuthContext must be used within AuthProvider")
  return ctx
}

export function AuthProvider({
  children,
  onLogout,
}: {
  children: ReactNode
  onLogout: () => void
}) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!getToken())
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [permissionsReady, setPermissionsReady] = useState(false)

  /** 拉取并缓存当前用户的菜单/按钮权限 */
  const loadPermissions = useCallback(() => {
    loadAndCachePermissions()
      .then(() => setPermissionsReady(true))
      .catch(() => setPermissionsReady(true))
  }, [])

  const clearSessionScopedCaches = useCallback(() => {
    useUserStore.getState().clearUsers()
    useFeedStore.getState().clearFeeds()
    usePostStore.getState().clearPosts()
    invalidateCache("user:u-self")
    clearCommunitySelfCache()
  }, [])

  /** 从服务端获取头像，失败则根据用户名生成 */
  const fetchAvatar = useCallback((user: StoredUser | null) => {
    if (!user) {
      setAvatarUrl(null)
      return
    }
    // 优先使用已存储的头像（登录时从 LoginResponse 保存的）
    if (user.avatar) {
      setAvatarUrl(user.avatar)
    } else {
      // 没有存储头像时，先设一个基于用户名的生成头像
      const fallback = generateAvatarDataUri(getDisplayName(user))
      setAvatarUrl(fallback)
    }

    // 再尝试从服务端拉取最新头像（用户可能更新过）
    // 注意：profile 接口返回 { userInfo: {...}, stats: {...} } 嵌套结构
    getUserInfo()
      .then((resp) => {
        const info = (resp as unknown as { userInfo: UserInfoResponse }).userInfo ?? resp
        const avatar = info.avatar
        if (avatar) {
          setAvatarUrl(avatar)
          // 同步更新 currentUser state 和 localStorage
          setCurrentUser((prev) => prev ? { ...prev, avatar } : prev)
          const stored = getStoredUser()
          if (stored) {
            setStoredUser({ ...stored, avatar })
          }
        }
        const stored = getStoredUser()
        if (stored) {
          const nextUser = {
            ...stored,
            nick: info.nick || stored.nick,
            phone: info.phone || stored.phone,
            email: info.email || stored.email,
            avatar: avatar || stored.avatar,
          }
          setStoredUser(nextUser)
          setCurrentUser(nextUser)
        }
      })
      .catch(() => {
        // 保持已有头像，静默处理
      })
  }, [])

  /* ── Init: check for existing token ── */
  useEffect(() => {
    if (getToken()) {
      const user = getStoredUser()
      setIsLoggedIn(true)
      setCurrentUser(user)
      fetchAvatar(user)
      loadPermissions()
    }
  }, [fetchAvatar, loadPermissions])

  /* ── Global 401 listener: token expired → logout + show login ── */
  useEffect(() => {
    const handleExpired = () => {
      setIsLoggedIn(false)
      setCurrentUser(null)
      setAvatarUrl(null)
      setPermissionsReady(false)
      removeToken()
      clearPermissionsCache()
      clearSessionScopedCaches()
      setShowLoginModal(true)
    }
    window.addEventListener("auth:expired", handleExpired)
    return () => window.removeEventListener("auth:expired", handleExpired)
  }, [clearSessionScopedCaches])

  const openLogin = useCallback(() => setShowLoginModal(true), [])
  const closeLogin = useCallback(() => setShowLoginModal(false), [])

  const onLoginSuccess = useCallback(() => {
    const user = getStoredUser()
    clearSessionScopedCaches()
    setIsLoggedIn(true)
    setCurrentUser(user)
    fetchAvatar(user)
    setShowLoginModal(false)
    // 拉取用户菜单/按钮权限（缓存到 localStorage）
    loadPermissions()
    // 埋点分析：关联用户 ID
    if (user?.userId) {
      analytics.login(user.userId)
      analytics.setProfile({ name: getDisplayName(user) })
    }
  }, [clearSessionScopedCaches, fetchAvatar, loadPermissions])

  const logout = useCallback(() => {
    setIsLoggedIn(false)
    setCurrentUser(null)
    setAvatarUrl(null)
    setPermissionsReady(false)
    removeToken()
    clearPermissionsCache()
    clearSessionScopedCaches()
    analytics.logout() // 埋点分析：清除用户关联
    useChatStore.getState().resetChat()
    onLogout()
  }, [clearSessionScopedCaches, onLogout])

  const refreshAvatar = useCallback(() => {
    fetchAvatar(currentUser)
  }, [fetchAvatar, currentUser])

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        currentUser,
        avatarUrl,
        showLoginModal,
        openLogin,
        closeLogin,
        onLoginSuccess,
        logout,
        refreshAvatar,
        permissionsReady,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
